import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { liveUpdates } from '@/mocks/live';
import { siteInfo } from '@/mocks/site';

const NAV_LINKS = [
  { label: '首页', to: '/' },
  { label: '话题库', to: '/topics' },
  { label: '关于我们', to: '/about' },
  { label: '常见问题', to: '/faq' },
  { label: '联系我们', to: '/contact' },
];

const BrandMark = () => (
  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
    <i className="ri-fire-line text-lg" aria-hidden="true" />
  </span>
);

interface NavbarProps {
  brandAsH1?: boolean;
}

export default function Navbar({ brandAsH1 = false }: NavbarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [liveIndex, setLiveIndex] = useState(0);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const timer = window.setInterval(() => {
      setLiveIndex((prev) => (prev + 1) % liveUpdates.length);
    }, 3200);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const activeLive = liveUpdates[liveIndex];

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = query.trim();
    navigate(value ? `/topics?q=${encodeURIComponent(value)}` : '/topics');
    setQuery('');
  };

  const BrandTag = brandAsH1 ? 'h1' : 'span';

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-primary-600 text-background-50">
        <div className="mx-auto flex max-w-shell items-center gap-4 px-4 py-1.5 md:px-6">
          <span className="flex shrink-0 items-center gap-1.5 text-xs font-medium">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-flashlight-fill animate-live text-[10px]" aria-hidden="true" />
            </span>
            实时上新
          </span>
          <div className="hidden min-w-0 flex-1 items-center gap-2 text-xs sm:flex">
            <span className="truncate opacity-95">{activeLive ? activeLive.text : ''}</span>
            <span className="shrink-0 rounded-full bg-background-50/20 px-2 py-0.5 text-[10px]">
              {activeLive ? activeLive.time : ''}
            </span>
          </div>
          <span className="ml-auto hidden shrink-0 text-xs opacity-90 lg:block">{siteInfo.workingHours}</span>
        </div>
      </div>

      <div className="border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-shell items-center gap-4 px-4 py-3 md:px-6">
          <Link to="/" className="flex items-center gap-2.5" aria-label={`${siteInfo.name} 首页`}>
            <BrandMark />
            <span className="flex flex-col leading-none">
              <BrandTag className="font-heading text-lg font-bold tracking-tight text-foreground-950">
                {siteInfo.name}
              </BrandTag>
              <span className="mt-0.5 hidden text-[11px] text-foreground-600 sm:block">{siteInfo.slogan}</span>
            </span>
          </Link>

          <nav className="ml-2 hidden items-center gap-1 lg:flex" aria-label="主导航">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
                  isActive(link.to)
                    ? 'bg-primary-100 font-medium text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="ml-auto hidden items-center md:flex" role="search">
            <label className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm focus-within:border-primary-400">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" aria-hidden="true" />
              </span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索话题、人物或栏目"
                aria-label="站内搜索"
                className="w-32 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500 lg:w-44"
              />
            </label>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="ml-auto flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} aria-hidden="true" />
          </button>
        </div>

        {menuOpen && (
          <nav className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden" aria-label="移动端导航">
            <ul className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={`block rounded-md px-3 py-2 text-sm ${
                      isActive(link.to)
                        ? 'bg-primary-100 font-medium text-primary-700'
                        : 'text-foreground-700'
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <form onSubmit={submitSearch} className="mt-3 flex items-center gap-2" role="search">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" aria-hidden="true" />
              </span>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索话题"
                aria-label="站内搜索"
                className="flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm outline-none focus:border-primary-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-sm text-background-50"
              >
                搜索
              </button>
            </form>
          </nav>
        )}
      </div>
    </header>
  );
}